import { LayoutService } from '@/layout/service/layout.service';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { debounceTime, Subscription } from 'rxjs';
import { StackedChartComponent } from '@/shared/ui-components/charts/stacked-chart/stacked-chart.component';
import { CardComponent } from '@/shared/ui-components/card/card.component';

@Component({
    standalone: true,
    selector: 'app-revenue-stream-widget',
    imports: [StackedChartComponent, CardComponent, SkeletonModule],
    templateUrl: './revenuestreamwidget.html'
})
export class RevenueStreamWidget implements OnInit, OnChanges, OnDestroy {
    @Input() title = 'Revenue Stream';
    @Input() chartData: { labels: string[]; datasets: unknown[] } | null = null;
    @Input() chartOptions: Record<string, unknown> | null = null;
    @Input() loading = false;
    @Input() error: unknown = null;

    resolvedChartOptions: Record<string, unknown> | null = null;
    private subscription?: Subscription;

    constructor(public layoutService: LayoutService) {}

    ngOnInit(): void {
        this.initChartOptions();
        this.subscription = this.layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
            this.initChartOptions();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['chartOptions']) {
            this.initChartOptions();
        }
    }

    get hasChartData(): boolean {
        return !!this.chartData?.labels?.length && !!this.chartData?.datasets?.length;
    }

    private initChartOptions(): void {
        if (this.chartOptions) {
            this.resolvedChartOptions = this.chartOptions;
            return;
        }

        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');

        this.resolvedChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        padding: 30,
                        boxWidth: 10,
                        boxHeight: 10,
                        color: textColor,
                        font: {
                            size: 15,
                            weight: '500',
                        },
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: {
                        color: 'transparent',
                        borderColor: 'transparent',
                    },
                },
                y: {
                    stacked: true,
                    ticks: {
                        font: {
                            size: 12,
                            weight: '500',
                            family: 'Inter, Arial, sans-serif',
                        },
                        padding: 20,
                    },
                    grid: {
                        borderColor: 'transparent',
                        drawTicks: false,
                        tickLength: 0,
                    },
                },
            },
        };
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}
